import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

// -------- Language dictionaries ----------------------------------------------
const STRINGS = {
  sq: {
    nav: {
      platform: "Platforma",
      pricing: "Çmimet",
      integrations: "Integrimet",
      about: "Rreth Nesh",
      blog: "Blog",
      contact: "Kontakt",
      login: "Hyr",
      requestDemo: "Kërko Demo",
      seeAllIntegrations: "Shiko të gjitha integrimet",
      mainNav: "Navigimi kryesor",
      openMenu: "Hap menynë",
      solutions: "Zgjidhje",
    },
    platformMenu: {
      overview: "Pamje e Përgjithshme",
      pms: "Program për Hotele (PMS)",
      bookingEngine: "Booking Engine",
      channelManager: "Channel Manager",
      housekeeping: "Housekeeping",
      pos: "POS",
      revenue: "Menaxhimi i të Ardhurave",
      integrations: "Integrimet",
    },
    footer: {
      tagline:
        "Program hotelerie për hotele, resorte dhe apartamente. Sinkronizim i rezervimeve me Booking.com, Airbnb, Expedia dhe kanalet kryesore.",
      platform: "Platforma",
      company: "Kompania",
      startToday: "Filloni",
      startBlurb: "Provojeni Akomoda me një demo falas. Konfigurim brenda 48 orësh.",
      requestDemo: "Kërko Demo",
      contactUs: "Na Kontaktoni",
      rights: "Të gjitha të drejtat e rezervuara.",
      region: "Shqipëri · Ballkan · Evropë",
    },
    forms: {
      sending: "Duke dërguar…",
      contactSuccess: "Mesazhi u dërgua. Do t'ju kontaktojmë së shpejti.",
      demoSuccess: "Kërkesa u dërgua. Do t'ju kontaktojmë së shpejti.",
      networkError: "Nuk arritëm të lidhemi me serverin. Kontrolloni lidhjen ose provoni pas pak.",
      generic: "Diçka shkoi keq. Ju lutemi provoni përsëri.",
    },
    common: {
      lang: "Gjuha",
      langShort: "SQ",
      chooseRegion: "Zgjidh rajonin",
    },
  },
  en: {
    nav: {
      platform: "Platform",
      pricing: "Pricing",
      integrations: "Integrations",
      about: "About",
      blog: "Blog",
      contact: "Contact",
      login: "Log in",
      requestDemo: "Request Demo",
      seeAllIntegrations: "See all integrations",
      mainNav: "Main navigation",
      openMenu: "Open menu",
      solutions: "Solutions",
    },
    platformMenu: {
      overview: "Platform Overview",
      pms: "Hotel PMS",
      bookingEngine: "Booking Engine",
      channelManager: "Channel Manager",
      housekeeping: "Housekeeping",
      pos: "POS",
      revenue: "Revenue Management",
      integrations: "Integrations",
    },
    footer: {
      tagline:
        "Professional hotel management software for hotels, resorts and apartments. Real-time sync with Booking.com, Airbnb, Expedia and major channels.",
      platform: "Platform",
      company: "Company",
      startToday: "Get started today",
      startBlurb: "Try Akomoda with a free demo. Fully configured within 48 hours.",
      requestDemo: "Request Demo",
      contactUs: "Contact Us",
      rights: "All rights reserved.",
      region: "Albania · Balkans · Europe",
    },
    forms: {
      sending: "Sending…",
      contactSuccess: "Message sent. We'll get back to you shortly.",
      demoSuccess: "Request received. We'll be in touch shortly.",
      networkError: "Couldn't reach the server. Check your connection and try again.",
      generic: "Something went wrong. Please try again.",
    },
    common: {
      lang: "Language",
      langShort: "EN",
      chooseRegion: "Choose your region",
    },
  },
};

const I18nContext = createContext(null);
const SUPPORTED = ["sq", "en"];

function setCookie(name, value, days = 365) {
  const exp = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${exp}; path=/; SameSite=Lax`;
}

/** Detect language from URL prefix. `/en/…` → "en", everything else → "sq". */
export function langFromPath(pathname) {
  if (!pathname) return "sq";
  const m = /^\/(en)(\/|$)/.exec(pathname);
  return m ? m[1] : "sq";
}

/** Strip `/en` prefix and return the canonical (language-neutral) path. */
export function stripLangPrefix(pathname) {
  const stripped = (pathname || "/").replace(/^\/en(?=\/|$)/, "");
  if (!stripped) return "/";
  // Remove trailing slash except for root
  return stripped.length > 1 ? stripped.replace(/\/$/, "") : stripped;
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    if (typeof window !== "undefined") {
      const urlLang = langFromPath(window.location.pathname);
      if (urlLang !== "sq") return urlLang;
      const stored = localStorage.getItem("akm_lang");
      if (stored && SUPPORTED.includes(stored)) return stored;
    }
    return "sq";
  });

  const setLang = (next) => {
    if (!SUPPORTED.includes(next)) return;
    setLangState(next);
  };

  useEffect(() => {
    localStorage.setItem("akm_lang", lang);
    setCookie("akm_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const t = STRINGS[lang] || STRINGS.sq;

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export const useI18n = () => useContext(I18nContext);

/**
 * Prefix a path with the current language when in EN mode.
 * Example: useLangPath()("/cmimet") → "/en/cmimet" when lang="en".
 * External URLs (starting with http/mailto/tel) are returned untouched.
 */
export function useLangPath() {
  const ctx = useContext(I18nContext);
  const lang = ctx?.lang || "sq";
  return (path) => {
    if (!path) return path;
    if (/^(https?:|mailto:|tel:|#)/i.test(path)) return path;
    if (lang === "sq") return path;
    // Already prefixed?
    if (path === "/en" || path.startsWith("/en/")) return path;
    if (path === "/") return "/en";
    return `/en${path.startsWith("/") ? "" : "/"}${path}`;
  };
}

/**
 * Returns the canonical language-neutral pathname (with `/en` stripped).
 * Useful for the language switcher.
 */
export function useCanonicalPath() {
  const location = useLocation();
  return stripLangPrefix(location.pathname);
}
